import React, { useRef, useState } from 'react';
import classNames from 'classnames';
import Icon from 'components/styleguide/icon/Icon';
import Radio from 'components/styleguide/radio/Radio';
import { useAutoSave } from 'components/styleguide/hooks/useAutoSave';

const styles = require('./SelectionButton.module.scss');

export type SelectionButtonContext = 'talent' | 'company';
export type SelectionButtonState = 'simple' | 'checkmark' | 'radio';

export interface SelectionButtonProps {
  autoSaveUrl?: string;
  context?: SelectionButtonContext;
  disabled?: boolean;
  fullWidth?: boolean;
  label: string;
  name?: string;
  onChange?: (selected: boolean) => void;
  selected?: boolean;
  state?: SelectionButtonState;
  value?: string;
}

const SelectionButton: React.FC<SelectionButtonProps> = ({
  autoSaveUrl,
  context = 'talent',
  disabled = false,
  fullWidth = false,
  label,
  name,
  onChange,
  selected = false,
  state = 'simple',
  value,
}) => {
  const { save } = useAutoSave({ url: autoSaveUrl || '', name: name || '', debounceMs: 0 });
  const isToggleable = state === 'checkmark';
  const isRadio = state === 'radio';
  const radioInputRef = useRef<HTMLInputElement>(null);

  const isControlled = onChange !== undefined;
  const [internalSelected, setInternalSelected] = useState(selected);
  const resolvedSelected = isControlled ? selected : internalSelected;

  const commit = (nextSelected: boolean) => {
    if (!isControlled) setInternalSelected(nextSelected);
    onChange?.(nextSelected);
    if (autoSaveUrl && !isToggleable && nextSelected && value !== undefined) {
      save(value);
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled) return;

    if (isRadio) {
      if (event.target instanceof HTMLInputElement) return;
      radioInputRef.current?.click();
      return;
    }

    if (!isToggleable && resolvedSelected) return;
    commit(!resolvedSelected);
  };

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    commit(event.target.checked);
  };

  return (
    <>
      <button
        type="button"
        className={classNames(
          styles['selection-button'],
          styles[`selection-button--${state}`],
          styles[`selection-button--${context}`],
          {
            [styles['selection-button--selected']]: resolvedSelected,
            [styles['selection-button--disabled']]: disabled,
            [styles['selection-button--full-width']]: fullWidth,
          },
        )}
        onClick={handleClick}
        disabled={disabled}
        aria-pressed={isRadio ? undefined : resolvedSelected}
      >
        {isRadio && (
          <span className={styles['selection-button__radio-slot']} aria-hidden="true">
            <Radio
              context={context}
              checked={resolvedSelected}
              name={name}
              value={value || ''}
              inputRef={radioInputRef}
              onChange={handleRadioChange}
            />
          </span>
        )}
        <span className={styles['selection-button__label']}>{label}</span>
        {isToggleable && resolvedSelected && (
          <Icon name="check" size="sm" color={context + "-border-base"} aria-hidden="true" />
        )}
      </button>
      {!isRadio && resolvedSelected && name && value !== undefined && (
        <input type="hidden" name={name} value={value} />
      )}
    </>
  );
};

export default SelectionButton;
